import { useMemo } from 'react'

import {
  AnnotationPopover,
  bucketEventDateGroups,
  buildAnnotationAriaLabel,
  buildDayAlignedTicks,
  groupChannelsByEventDate,
  pickAnnotationBucketUnit,
} from '../../components/charts'
import type { ChartAnnotation } from '../../components/charts'

import type { useMetricChannels } from './useMetricChannels'

type MetricChannels = ReturnType<typeof useMetricChannels>

export function useFollowersAnnotations({
  displayedChannels,
  channels,
  startDate,
  endDate,
}: {
  displayedChannels: MetricChannels['displayedChannels']
  channels: MetricChannels['channels']
  startDate: Date
  endDate: Date
}): {
  followersAnnotations: ChartAnnotation[]
  followersAnnotationTicks: ReturnType<typeof buildDayAlignedTicks> | undefined
} {
  // Flag each channel's data-collection start on the over-time line.
  // `groupChannelsByEventDate` drops dates before the range, so a flag shows
  // only for a channel whose line starts partway across the chart.
  const followersAnnotations = useMemo<ChartAnnotation[]>(() => {
    const eventDateById = new Map(
      displayedChannels.map((ch) => [
        ch.channel.id,
        ch.firstChannelDataCollectedAt,
      ]),
    )
    const groups = groupChannelsByEventDate(
      channels,
      eventDateById,
      startDate,
      endDate,
    )
    const unit = pickAnnotationBucketUnit(startDate, endDate)
    return bucketEventDateGroups(groups, unit).map((bucket) => ({
      id: bucket.id,
      date: new Date(bucket.positionTimestamp),
      lineDates: bucket.dayTimestamps.map((ts) => new Date(ts)),
      // Multiple lines with different start dates → a single overlay would mislead.
      showNoDataOverlay: false,
      ariaLabel: buildAnnotationAriaLabel(bucket, 'started collecting data'),
      content: (
        <AnnotationPopover>
          {bucket.days.map((day) => (
            <AnnotationPopover.DateGroup key={day.id} date={day.eventDate}>
              {day.channels.map((channel) => (
                <AnnotationPopover.ChannelRow
                  key={channel.id}
                  channel={channel}
                >
                  started collecting data
                </AnnotationPopover.ChannelRow>
              ))}
            </AnnotationPopover.DateGroup>
          ))}
        </AnnotationPopover>
      ),
    }))
  }, [displayedChannels, channels, startDate, endDate])

  // Align ticks to days so labels sit under the flags; default layout otherwise.
  const followersAnnotationTicks = useMemo(
    () =>
      followersAnnotations.length > 0
        ? buildDayAlignedTicks(startDate, endDate)
        : undefined,
    [followersAnnotations.length, startDate, endDate],
  )

  return { followersAnnotations, followersAnnotationTicks }
}
